/**
 * AnalysisContext — Global analysis state that survives navigation.
 *
 * Backend persists job status in ES, so even a full page refresh recovers state.
 * This context keeps an in-memory mirror of each tender's analysis, polls the
 * backend while a job is running, and fetches phase results incrementally.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { bidAgentsAPI } from '../services/api';

const AnalysisContext = createContext();

const POLL_INTERVAL = 3000; // 3 seconds

const DEFAULT_STATE = {
  status: 'idle', // idle | running | complete | failed
  completedPhases: [],
  runningPhases: [],
  failedPhases: [],
  progress: [],
  phases: {},
  error: null,
  startedAt: null,
};

export function AnalysisProvider({ children }) {
  const [analyses, setAnalyses] = useState({});
  const pollingRefs = useRef({}); // tenderId → intervalId
  const analysesRef = useRef(analyses);

  // Keep ref in sync so polling callbacks see latest state
  useEffect(() => {
    analysesRef.current = analyses;
  }, [analyses]);

  const updateAnalysis = useCallback((tenderId, updates) => {
    setAnalyses(prev => ({
      ...prev,
      [tenderId]: { ...(prev[tenderId] || DEFAULT_STATE), ...updates },
    }));
  }, []);

  // ---------- Polling logic ----------

  const stopPolling = useCallback((tenderId) => {
    if (pollingRefs.current[tenderId]) {
      clearInterval(pollingRefs.current[tenderId]);
      delete pollingRefs.current[tenderId];
    }
  }, []);

  const startPolling = useCallback((tenderId) => {
    stopPolling(tenderId);

    const poll = async () => {
      try {
        const status = await bidAgentsAPI.getAnalysisStatus(tenderId);

        // Detect newly completed phases to trigger result fetch
        const prevCompleted = new Set(
          analysesRef.current[tenderId]?.completedPhases || []
        );
        const newlyCompleted = (status.completed_phases || []).filter(
          p => !prevCompleted.has(p)
        );

        updateAnalysis(tenderId, {
          status: status.status === 'complete' ? 'complete' : status.status,
          completedPhases: status.completed_phases || [],
          runningPhases: status.running_phases || [],
          failedPhases: status.failed_phases || [],
          progress: status.recent_progress || [],
          startedAt: status.started_at
            ? new Date(status.started_at).getTime()
            : analysesRef.current[tenderId]?.startedAt || null,
          error: status.error,
        });

        // Fetch latest phase results when new phases complete
        if (newlyCompleted.length > 0) {
          try {
            const results = await bidAgentsAPI.getTenderResults(tenderId);
            if (results?.phases) {
              updateAnalysis(tenderId, { phases: results.phases });
            }
          } catch (e) {
            console.error('Error fetching phase results:', e);
          }
        }

        // Stop polling when done
        if (status.status === 'complete' || status.status === 'failed') {
          stopPolling(tenderId);
          // Final result fetch for completeness
          if (status.status === 'complete') {
            try {
              const results = await bidAgentsAPI.getTenderResults(tenderId);
              if (results?.phases) {
                updateAnalysis(tenderId, { phases: results.phases });
              }
            } catch (e) {
              console.error('Error fetching final results:', e);
            }
          }
        }
      } catch (err) {
        console.error('Polling error for', tenderId, err);
      }
    };

    // Poll immediately, then at interval
    poll();
    pollingRefs.current[tenderId] = setInterval(poll, POLL_INTERVAL);
  }, [updateAnalysis, stopPolling]);

  // ---------- Public API ----------

  /**
   * Start a new analysis for a tender. Fire-and-forget + polling.
   */
  const startAnalysis = useCallback(async (tenderId) => {
    const current = analysesRef.current[tenderId];
    if (current?.status === 'running') return; // already running

    updateAnalysis(tenderId, {
      ...DEFAULT_STATE,
      status: 'running',
      startedAt: Date.now(),
    });

    try {
      const response = await bidAgentsAPI.startAnalysisBackground(tenderId);

      if (response.status === 'already_complete') {
        // All phases cached — fetch results directly, no rerun needed
        const results = await bidAgentsAPI.getTenderResults(tenderId);
        const phaseKeys = Object.keys(results?.phases || {});
        updateAnalysis(tenderId, {
          status: 'complete',
          completedPhases: phaseKeys,
          runningPhases: [],
          failedPhases: [],
          phases: results?.phases || {},
        });
        return;
      }

      startPolling(tenderId);
    } catch (err) {
      console.error('Error starting analysis:', err);
      updateAnalysis(tenderId, { status: 'failed', error: err.message });
    }
  }, [updateAnalysis, startPolling]);

  /**
   * Initialize analysis state for a tender. Call on page mount.
   * - If backend says running → start polling
   * - If backend says complete → load results
   * - If idle → do nothing
   */
  const initAnalysis = useCallback(async (tenderId) => {
    // Don't double-poll
    if (pollingRefs.current[tenderId]) return;

    const current = analysesRef.current[tenderId];

    // Trust 'complete' state in memory — no need to re-check the backend.
    if (current?.status === 'complete') return;

    // For 'running' state we previously short-circuited and restarted
    // polling without verifying the backend. That left the navbar dot
    // pulsing whenever a stuck-running entry persisted in memory
    // (worker died mid-flight, page navigated away during start, etc.).
    // Now we always re-verify on init and let the backend be truth.
    try {
      const status = await bidAgentsAPI.getAnalysisStatus(tenderId);

      if (status.status === 'running') {
        updateAnalysis(tenderId, {
          status: 'running',
          completedPhases: status.completed_phases || [],
          runningPhases: status.running_phases || [],
          failedPhases: status.failed_phases || [],
          progress: status.recent_progress || [],
          startedAt: status.started_at
            ? new Date(status.started_at).getTime()
            : Date.now(),
        });
        startPolling(tenderId);
      } else if (status.status === 'complete') {
        const results = await bidAgentsAPI.getTenderResults(tenderId);
        updateAnalysis(tenderId, {
          status: 'complete',
          completedPhases: status.completed_phases || [],
          runningPhases: [],
          failedPhases: status.failed_phases || [],
          phases: results?.phases || {},
          startedAt: status.started_at
            ? new Date(status.started_at).getTime()
            : null,
        });
      } else {
        // Backend says idle (or unknown). If memory had a stale 'running',
        // clear it so the navbar dot stops pulsing.
        if (current?.status === 'running') {
          updateAnalysis(tenderId, {
            status: 'idle',
            runningPhases: [],
            error: null,
          });
        }
      }
    } catch (err) {
      // Background init across many bookmarked tenders — non-actionable, keep
      // it out of the error console (debug only).
      console.debug('initAnalysis skipped for', tenderId, err?.message || err);
      // On a network error, if we had a stale 'running' that's > 30 min
      // old, treat as failed so the dot stops pulsing. Fresh runs are
      // preserved.
      if (
        current?.status === 'running' &&
        current.startedAt &&
        Date.now() - current.startedAt > 30 * 60 * 1000
      ) {
        updateAnalysis(tenderId, {
          status: 'failed',
          error: 'Status check failed and run is older than 30 min',
        });
      }
    }
  }, [updateAnalysis, startPolling]);

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
    };
  }, []);

  // Derived: is any analysis currently running?
  const runningAnalyses = Object.entries(analyses)
    .filter(([, a]) => a.status === 'running')
    .map(([tid]) => tid);
  const anyRunning = runningAnalyses.length > 0;

  return (
    <AnalysisContext.Provider
      value={{ analyses, startAnalysis, initAnalysis, anyRunning, runningAnalyses }}
    >
      {children}
    </AnalysisContext.Provider>
  );
}

/**
 * Hook for a specific tender's analysis state.
 * Call initAnalysis in useEffect to recover state on mount.
 */
export function useAnalysis(tenderId) {
  const ctx = useContext(AnalysisContext);
  const analysis = tenderId ? ctx.analyses[tenderId] : null;

  return {
    analysis: analysis || { ...DEFAULT_STATE },
    startAnalysis: () => ctx.startAnalysis(tenderId),
    initAnalysis: () => ctx.initAnalysis(tenderId),
  };
}

/**
 * Hook for global analysis awareness (NavBar, Responses list).
 */
export function useAnalysisGlobal() {
  const ctx = useContext(AnalysisContext);
  return {
    analyses: ctx.analyses,
    anyRunning: ctx.anyRunning,
    runningAnalyses: ctx.runningAnalyses,
    startAnalysis: ctx.startAnalysis,
    initAnalysis: ctx.initAnalysis,
  };
}
